import { useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Grid,
  Link,
  Paper,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import logo from "../../../assets/images/logo.jpeg";
import register from "../../../assets/images/register.png";

export default function ResumeUpload({ data, updateData, onBack, onNext }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 3 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: "File size exceeds 3MB. Please upload a smaller file.",
          severity: "error",
        });
        return;
      }
      setFile(selectedFile);
      updateData({ resume: selectedFile });
      setShowError(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.size > 3 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: "File size exceeds 3MB. Please upload a smaller file.",
          severity: "error",
        });
        return;
      }
      setFile(droppedFile);
      updateData({ resume: droppedFile });
      setShowError(false);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!file) {
      setShowError(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      // Append all form data
      Object.keys(data).forEach(key => { 
        const value = data[key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

       await axios.post(
        "https://e1xaca.edoubleone.net/api/opportunities",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSnackbar({
        open: true,
        message: "Application submitted successfully!",
        severity: "success",
      });
      

      // Move to next step or show success message
      onNext(); 
      // You can modify this to handle the success state
    } catch (error) {
      console.error("Submission error:", error);
      // setSnackbar({
      //   open: true,
      //   message: error.response?.data?.message || "Submission failed. Please try again.",
      //   severity: "error",
      // });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      {/* Left side - Image */}
      <Grid
        item
        xs={false}
        sm={false}
        md={6}
        sx={{
          backgroundImage: `url(${register})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: { xs: "none", md: "block" },
        }}
      />

      {/* Right side - Form */}
      <Grid item xs={12} md={6} component={Paper} elevation={0} square>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "auto",
            px: 4,
            maxWidth: 500,
            mx: "auto",
            py: 4,
          }}
        >
          {/* Logo and Header */}
          <Box sx={{ mb: 6, width: "100%", maxWidth: 120 }}>
            <img
              src={logo}
              alt="E1X Academy Logo"
              width={120}
              height={60}
              style={{ objectFit: "contain" }}
            />
          </Box>

          <Typography component="h1" variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
            Upload your resume
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
            To help us match you with the perfect role, please upload your most recent resume or CV.
          </Typography>

          {/* File Upload Area */}
          <Box sx={{ width: "100%", mb: 6 }}>
            <Box
              sx={{
                border: `1px dashed ${isDragging ? "#E84A1C" : "#ccc"}`,
                borderRadius: 1,
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isDragging ? "rgba(232, 74, 28, 0.1)" : "transparent",
                transition: "all 0.2s ease",
                cursor: "pointer",
                minHeight: 180,
              }}
              onClick={handleChooseFile}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx"
              />

              <CloudUploadIcon sx={{ fontSize: 40, color: "#E84A1C", mb: 2 }} />

              <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                Drag & Drop file here
              </Typography>

              <Typography variant="body1" sx={{ mb: 2 }}>
                or
              </Typography>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#E84A1C",
                  "&:hover": { backgroundColor: "#D43D10" },
                  px: 4,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChooseFile();
                }}
              >
                Choose file
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Supported formats: PDF, DOC, DOCX (max 3MB)
              </Typography>

              {file && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
            </Box>

            {showError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Please upload a resume before continuing.
              </Typography>
            )}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#E84A1C",
                color: "#E84A1C",
                "&:hover": { borderColor: "#D43D10" },
                px: 4,
              }}
              onClick={onBack}
              disabled={isSubmitting}
            >
              Back
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: "#D15424",
                "&:hover": { backgroundColor: "#E84A1C" },
                px: 4,
                minWidth: 120,
              }}
              onClick={handleSubmit}
              disabled={!file || isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Submit Application"
              )}
            </Button>
          </Box>

          {/* Sign In Link */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Already have an account?
            </Typography>
            <Link
              href="/talents/login"
              variant="body2"
              sx={{ color: "#E84A1C", textDecoration: "none" }}
            >
              Sign In
            </Link>
          </Box>

          <Divider sx={{ mt: 2 }} />
        </Box>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}